import React, { useState, useEffect, useMemo } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import { motion } from 'framer-motion';
import { Link, useNavigate} from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { useLessons, useProgress, useAssignments } from '../../hooks/queries';

const CoursesPage = () => {
  const [selectedLevel, setSelectedLevel] = useState('all');
  const navigate = useNavigate(); // 🟢 初始化 hook

  // 🟢 [P2] React Query：课程表 / 进度 / 作业改为缓存化读取，切回页面无闪烁
  const { data: courseList = [], isLoading: loadingLessons } = useLessons();
  const { data: progressRes, isLoading: loadingProgress } = useProgress();
  const { data: assignments = [], isLoading: loadingAssignments } = useAssignments();
  // 🔴 [修复] 始终为对象，绝不为 undefined —— React Query 下 courseList 与 progressData
  // 独立加载，若 progressData 为 undefined 时 courseList 已就绪，下方 useMemo 会崩溃
  const progressData = progressRes?.progress || {};
  const loading = loadingLessons || loadingProgress || loadingAssignments;

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 9;

  const { user } = useAuth();

  const levels = [
    { id: 'all', name: 'All Levels', color: 'from-gray-500 to-gray-600', icon: '📚', badgeColor: 'bg-gray-600' },
    { id: 'core', name: 'Core', color: 'from-green-500 to-green-600', icon: '🌱', badgeColor: 'bg-green-500' },
    { id: 'stretch', name: 'Stretch', color: 'from-yellow-500 to-orange-500', icon: '🔥', badgeColor: 'bg-orange-500' },
    { id: 'genius', name: 'Genius', color: 'from-purple-500 to-purple-600', icon: '🧠', badgeColor: 'bg-purple-600' }
  ];

  // 🟢 [新增] 辅助函数：计算剩余天数 (修复 NaN 问题)
  const getDaysLeft = (dueDateData) => {
    if (!dueDateData) return 0;
    
    let due;
    // 兼容 Firestore Timestamp (_seconds) 或 ISO 字符串
    if (dueDateData._seconds) {
      due = new Date(dueDateData._seconds * 1000);
    } else {
      due = new Date(dueDateData);
    }

    // 如果日期无效，返回 0
    if (isNaN(due.getTime())) return 0;

    const now = new Date();
    const diffTime = due - now;
    // 向上取整，不足1天算1天，过期为负数
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // 数据获取已由上方 React Query hooks 处理

  // 切换等级时重置页码
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLevel]);

  // ======================================================
  // 2. 核心逻辑：合并课程与进度 (Merge Logic)
  // ======================================================
  
  // 辅助函数：判断大等级是否解锁
  const isLevelUnlocked = (levelId) => {
    if (!progressData) return levelId === 'core';
    if (levelId === 'all' || levelId === 'core') return true;
    
    if (levelId === 'stretch') {
      const core = progressData.coreProgress || {};
      // 只要完成数 >= 总数，或者后端有明确的 unlocked 字段
      return (core.completed || 0) >= (core.total || 12);
    }
    if (levelId === 'genius') {
      const stretch = progressData.stretchProgress || {};
      return (stretch.completed || 0) >= (stretch.total || 12);
    }
    return false;
  };

  // 🟢 [修复] Tab 锁状态必须与卡片解锁逻辑一致：
  // 每个等级的第一课始终解锁（见下方卡片规则），所以只要该等级有任意一节可玩的课，
  // Tab 就应可点击。此前 Tab 直接用 isLevelUnlocked（整级门槛）判定，导致
  // Stretch/Genius 明明能从 All Levels 进入却显示 🔒 锁定。
  const accessibleLevels = useMemo(() => {
    const set = new Set(['all', 'core']);
    courseList.forEach(lesson => {
      const rawProgress = progressData[`${lesson.level}Progress`] || {};
      const completed = rawProgress.completed || 0;
      const isFirstLesson = lesson.lessonNumber === 1;
      const isLessonUnlocked = isFirstLesson || (isLevelUnlocked(lesson.level) && (lesson.lessonNumber <= completed + 1));
      if (isLessonUnlocked && lesson.level) set.add(lesson.level);
    });
    return set;
  }, [courseList, progressData]);

  // 🟢 计算最终要显示的列表 (排序逻辑修复)
  const displayedLessons = useMemo(() => {
    if (!courseList.length) return [];

    // 1. 过滤等级
    let filtered = courseList;
    if (selectedLevel !== 'all') {
      filtered = courseList.filter(l => l.level === selectedLevel);
    }

    // 2. 映射进度状态
    const processedList = filtered.map(lesson => {
       const rawProgress = progressData[`${lesson.level}Progress`] || {};
       const lvlProgress = {
           completed: rawProgress.completed || 0, // 👈 核心修复：防止 undefined
           total: rawProgress.total || 0
       };       const isLvlUnlocked = isLevelUnlocked(lesson.level);
       
       // 判定单节课解锁逻辑
       // 规则：每个等级的第一课始终解锁（让进阶用户可以直接体验高难度）
       // 其余课程需要大等级解锁 + 按顺序完成
       const isFirstLesson = lesson.lessonNumber === 1;
       const isLessonUnlocked = isFirstLesson || (isLvlUnlocked && (lesson.lessonNumber <= lvlProgress.completed + 1));
       
       // 进度条
       const progressPercent = lesson.lessonNumber <= lvlProgress.completed ? 100 : 0;

       return {
         ...lesson,
         isUnlocked: isLessonUnlocked,
         progress: progressPercent
       };
    });

    // 3. 🟢 [新增] 排序逻辑
    // 优先级：Core (1) -> Stretch (2) -> Genius (3) -> 其他 (99)
    // 次级排序：Lesson Number (从小到大)
    const levelWeights = { 'core': 1, 'stretch': 2, 'genius': 3 };

    return processedList.sort((a, b) => {
        // A. 先比等级权重
        const levelA = a.level ? levelWeights[a.level.toLowerCase()] || 99 : 99;
        const levelB = b.level ? levelWeights[b.level.toLowerCase()] || 99 : 99;

        if (levelA !== levelB) {
            return levelA - levelB; // 权重小的在前
        }

        // B. 如果等级相同，比课号 (从小到大)
        return (a.lessonNumber || 0) - (b.lessonNumber || 0);
    });

  }, [courseList, selectedLevel, progressData]);

  // ======================================================
  // 3. 分页逻辑
  // ======================================================
  const totalItems = displayedLessons.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const currentLessons = displayedLessons.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    document.getElementById('courses-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // ======================================================
  // 4. 渲染
  // ======================================================
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 to-blue-300">
      <div className="text-2xl font-bold text-white flex items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-3"></div>
        Loading Learning Path...
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">📚 Vocabulary Courses</h1>
          <p className="text-xl text-gray-700">Choose your learning path and master new words!</p>
        </motion.div>

        {/* 🟢 Learning Tips — 移到页面顶部，让学生第一眼看到 70% 及格线 */}
        <motion.div initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} className="bg-white bg-opacity-30 backdrop-blur-lg rounded-2xl p-6 mb-8 text-gray-800 border border-white/40">
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">💡 Learning Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4">
              <div className="text-3xl mb-2">🎯</div>
              <h4 className="font-bold mb-1">Score 70% to Pass</h4>
              <p className="text-sm text-gray-700">Pass each lesson quiz with 70% or higher to unlock the next lesson</p>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl mb-2">🔄</div>
              <h4 className="font-bold mb-1">Review Daily</h4>
              <p className="text-sm text-gray-700">Keep your streak alive for bonus points</p>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl mb-2">🏆</div>
              <h4 className="font-bold mb-1">Win Badges</h4>
              <p className="text-sm text-gray-700">Collect them all by completing challenges</p>
            </div>
          </div>
        </motion.div>

        {/* Level Filter */}
        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="flex flex-wrap justify-center gap-4 mb-12">
          {levels.map((level) => {
            const unlocked = accessibleLevels.has(level.id);
            return (
              <motion.button
                key={level.id}
                whileHover={{ scale: unlocked ? 1.05 : 1 }}
                whileTap={{ scale: unlocked ? 0.95 : 1 }}
                onClick={() => unlocked && setSelectedLevel(level.id)}
                disabled={!unlocked}
                className={`px-6 py-3 rounded-full font-bold transition-all duration-200 relative ${
                  selectedLevel === level.id 
                    ? `bg-gradient-to-r ${level.color} text-white ring-4 ring-white ring-opacity-50` 
                    : unlocked 
                        ? 'bg-white bg-opacity-20 text-gray-800 hover:bg-opacity-30' 
                        : 'bg-gray-400 bg-opacity-50 text-gray-500 cursor-not-allowed'
                }`}
              >
                <span className="mr-2">{level.icon}</span>{level.name}
                {!unlocked && <span className="absolute -top-1 -right-1 text-lg">🔒</span>}
              </motion.button>
            );
          })}
        </motion.div>

        {/* 🟢 SECTION 1: LEARNING MODULES (Real Data) */}
        <motion.div 
            id="courses-section"
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ delay: 0.2 }} 
            className="mb-12"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentLessons.length > 0 ? currentLessons.map((lesson, index) => {
                    const levelInfo = levels.find(l => l.id === lesson.level) || levels[1];
                    
                    // 🟢 真实预览标签 (后端 GET /lessons 应该返回 previewTags)
                    // 如果后端还没准备好 previewTags，回退显示 Generic Topic
                    const displayTags = (lesson.previewTags && lesson.previewTags.length > 0) 
                        ? lesson.previewTags 
                        : ['Vocabulary', 'Learning', '...']; 

                    return (
                        <motion.div 
                            key={lesson.id || `${lesson.level}-${lesson.lessonNumber}`} 
                            initial={{ scale: 0.95, opacity: 0 }} 
                            animate={{ scale: 1, opacity: 1 }} 
                            transition={{ delay: index * 0.05 }}
                            className={`
                                rounded-3xl p-6 shadow-lg border relative flex flex-col justify-between min-h-[260px]
                                ${lesson.isUnlocked 
                                    ? 'bg-sky-50 border-sky-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300' 
                                    : 'bg-gray-100 border-gray-200 opacity-80'}
                            `}
                        >
                            {/* Card Header */}
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-2xl font-extrabold text-slate-800">Lesson {lesson.lessonNumber}</h3>
                                {/* Badge */}
                                <div className={`${levelInfo.badgeColor} text-white px-3 py-1 rounded-full flex items-center gap-1 shadow-sm`}>
                                    <span className="text-sm">{levelInfo.icon}</span>
                                    <span className="text-sm font-bold capitalize">{levelInfo.name}</span>
                                </div>
                            </div>

                            {/* Subtitle */}
                            <div className="text-sm text-slate-500 font-medium mb-3">
                                {lesson.wordCount || 12} vocabulary words
                            </div>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-2 mb-6">
                                {displayTags.slice(0, 3).map((tag, i) => (
                                    <span key={i} className={`text-xs font-bold px-3 py-1.5 rounded-lg ${
                                        lesson.isUnlocked 
                                        ? 'bg-white text-slate-600 border border-slate-200' 
                                        : 'bg-slate-200 text-slate-400'
                                    }`}>
                                        {tag}
                                    </span>
                                ))}
                                {lesson.isUnlocked && <span className="text-xs font-bold px-2 py-1.5 text-slate-400">...</span>}
                            </div>

                            {/* Progress Section */}
                            <div className="mt-auto">
                                <div className="flex justify-between text-sm font-bold text-slate-600 mb-2">
                                    <span>Progress</span>
                                    <span>{lesson.progress}%</span>
                                </div>
                                
                                <div className="w-full bg-slate-200 rounded-full h-2 mb-5">
                                    <div 
                                        className={`${lesson.progress === 100 ? 'bg-green-500' : 'bg-blue-500'} h-2 rounded-full transition-all duration-500`} 
                                        style={{ width: `${lesson.progress}%` }}
                                    ></div>
                                </div>

                                {/* Action Button - Link to Real Lesson ID */}
                                {lesson.isUnlocked ? (
                                    <Link 
                                        // 🟢 使用真实的 Firestore Doc ID (lesson.id)
                                        to={`/lesson/${lesson.id}`} 
                                        className="block w-full bg-white hover:bg-slate-50 text-purple-600 font-bold py-3 rounded-xl text-center shadow-sm border border-purple-100 hover:shadow-md transition-all active:scale-95"
                                    >
                                        Start Lesson
                                    </Link>
                                ) : (
                                    <button disabled className="block w-full bg-slate-200 text-slate-400 font-bold py-3 rounded-xl text-center cursor-not-allowed">
                                        Locked
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    );
                }) : (
                    <div className="col-span-full text-center py-10 bg-white/20 rounded-2xl">
                        <p className="text-xl text-gray-600">No lessons found. Ask your admin to "Generate Curriculum".</p>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center mt-8 space-x-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 rounded-lg bg-white/40 text-gray-800 font-bold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/60 transition-colors"
                    >
                        <AppIcon name="chevron-left" /> Prev
                    </button>
                    
                    <span className="px-4 font-bold text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 rounded-lg bg-white/40 text-gray-800 font-bold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/60 transition-colors"
                    >
                        Next <AppIcon name="chevron-right" />
                    </button>
                </div>
            )}
        </motion.div>

        {/* 🟢 Assignments Section (保持不变) */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mb-12">
             <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center">
                <span className="mr-2">👨‍🏫</span> Teacher Assignments
            </h2>
             {assignments.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {assignments.map((assignment, index) => {
                        // 🟢 [修复] 使用 getDaysLeft 计算天数
                        const daysLeft = getDaysLeft(assignment.dueDate);
                        const isExpired = daysLeft < 0;
                        
                        return (
                            <motion.div 
                                key={assignment.id}
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white/90 backdrop-blur-lg rounded-2xl p-6 shadow-xl border-l-8 border-purple-500 relative overflow-hidden group hover:-translate-y-1 transition-transform"
                            >
                                <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                                    ASSIGNMENT
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2 pr-8 truncate">{assignment.title}</h3>
                                <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[2.5em]">
                                    {assignment.description || "No description provided."}
                                </p>
                                <div className="flex items-center justify-between text-sm text-gray-600 mb-4 bg-purple-50 p-2 rounded-lg">
                                    <div className="flex items-center">
                                        <AppIcon name="layer-group" className="mr-2 text-purple-500" />
                                        <span>{assignment.vocabularyIds?.length || 0} Words</span>
                                    </div>
                                    <div className={`font-bold ${isExpired ? 'text-red-500' : daysLeft <= 2 ? 'text-orange-500' : 'text-green-600'}`}>
                                        <AppIcon name="clock" className="mr-1" />
                                        {isExpired ? 'Expired' : `${daysLeft} days left`}
                                    </div>
                                </div>
                                <button 
                                    onClick={() => navigate(`/assignment/${assignment.id}`)} // 👈 这里修改
                                    className="block w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-xl text-center transition-colors shadow-md active:scale-95"
                                >
                                    Start Assignment
                                </button>
                            </motion.div>
                        );
                    })}
                </div>
            ) : (
                <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-10 text-center">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No Assignments Yet</h3>
                    <p className="text-gray-600">
                        When your teacher assigns vocabulary lists, they will appear here as cards.
                    </p>
                </div>
            )}
        </motion.div>

      </div>
    </div>
  );
};

export default CoursesPage;